/**
 * 12-Week Specialist Program
 * Advanced tactical fitness with strength + power focus
 * 5 days per week: Lower Power, Upper Power, Conditioning, Work Capacity, Endurance
 */

import type { WorkoutProgram, WorkoutTemplateDay, ProgramMetadata, ProgramPeriodizationModel } from '../types/program';

const metadata: ProgramMetadata = {
  id: 'specialist-12week',
  name: '12-Week Specialist Performance',
  description: 'Advanced tactical fitness with strength, power, and work capacity',
  level: 'specialist',
  duration: 12,
  daysPerWeek: 5,
  goals: ['tactical-performance', 'strength', 'power', 'work-capacity'],
  difficulty: 'advanced',
  version: '1.0.0',
  createdAt: new Date(),
  updatedAt: new Date(),
  isActive: true,
  hasRangeDay: false, // 5-day program; Day 6/7 are rest — no range/enhancement day content
};

// Specialist periodization (advanced progression with power focus)
const periodization: ProgramPeriodizationModel = {
  style: 'block',
  phases: [
    // Week 1 & 12: Testing
    {
      weeks: [1, 12],
      type: 'assessment',
      name: 'Performance Testing',
      focus: 'Establish baseline and demonstrate peak performance',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0
    },
    // Weeks 2-5: Strength-Power Development
    {
      weeks: [2, 3, 4, 5],
      type: 'accumulation',
      name: 'Strength-Power Building',
      focus: 'Building explosive strength and power output',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0,
      progression: {
        type: 'linear',
        startIntensity: 0.75,
        endIntensity: 0.82,
        weeklyIncrease: 0.0175
      },
      trainingParameters: {
        repRange: [3, 6],
        setsMultiplier: 1.0,
        restPeriod: '2-3min'
      }
    },
    // Week 6: Deload — drops intensity to ~70% and halves volume so the
    // CNS can clear before the Peak Performance block. Without this the
    // jump from 82% to 90% across W7-11 accumulates fatigue and the user
    // peaks at W8-9 instead of W11.
    {
      weeks: [6],
      type: 'deload',
      name: 'Recovery Deload',
      focus: 'Active recovery before peaking block',
      intensityMultiplier: 0.85,
      volumeMultiplier: 0.5,
      progression: {
        type: 'linear',
        startIntensity: 0.70,
        endIntensity: 0.70,
        weeklyIncrease: 0
      },
      trainingParameters: {
        repRange: [5, 8],
        setsMultiplier: 0.5,
        restPeriod: '2-3min'
      }
    },
    // Weeks 7-11: Peak Performance
    {
      weeks: [7, 8, 9, 10, 11],
      type: 'intensification',
      name: 'Peak Performance',
      focus: 'Maximizing strength and power for tactical demands',
      intensityMultiplier: 1.0,
      volumeMultiplier: 1.0,
      progression: {
        type: 'linear',
        startIntensity: 0.82,
        endIntensity: 0.90,
        weeklyIncrease: 0.016
      },
      trainingParameters: {
        repRange: [2, 4],
        setsMultiplier: 1.0,
        restPeriod: '3-5min'
      }
    }
  ]
};

// BASE TEMPLATES

// Splitting Week 1's "fitness test" into three distinct days. Cramming
// nine max-effort tests (bodyweight AMRAPs + 4 heavy lifts + a 3-mile
// ruck) into a single day is nonsense — every test bleeds into the next,
// max-effort numbers come out artificially low, and the user is wrecked.
// Tactical assessment weeks in real-world programs are spread across 3-4
// days so each test is truly fresh.
//
// Layout: Mon strength, Wed bodyweight + work capacity, Fri ruck.
// Tue/Thu/Sat/Sun rest.

const testingStrengthDay: WorkoutTemplateDay = {
  name: 'Strength Test',
  sessionType: 'assessment',
  estimatedDuration: 75,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '5 min', format: 'prep', details: 'Bike or row — thorough before max-effort testing' },
        { name: 'Ankle Rocks', sets: 1, reps: '10/side', format: 'prep', details: 'Dorsiflexion for front-squat depth' },
        { name: 'Hip Flexor Opener', duration: '30s/side', format: 'prep', details: 'Open the hips before squatting + hinging' },
        { name: 'Glute Bridge', sets: 2, reps: '10', format: 'prep', details: 'Posterior-chain activation' },
        { name: 'Band External Rotation', sets: 2, reps: '12/side', format: 'prep', details: 'Cuff prime before the incline + weighted pull-up' },
        { name: 'Warm-Up Ramp Sets', format: 'prep', details: 'Ramp each lift empty bar → 40% → 60% → 75% → 85% before your first max set. Do not test cold.' },
      ],
    },
    {
      name: 'Program Maxes',
      type: 'testing',
      exercises: [
        { name: 'Front Squat', sets: 1, reps: '3RM', intensity: 'Max', details: 'Work up to best 3-rep load. Take all the warm-up time you need.', sessionType: 'assessment' },
        { name: 'Trap Bar Deadlift', sets: 1, reps: '3RM', intensity: 'Max', details: 'Work up to best 3-rep load. Reset between reps.', sessionType: 'assessment' },
        { name: 'Incline Bench Press', sets: 1, reps: '5RM', intensity: 'Max', details: 'Work up to best 5-rep load with a spotter.', sessionType: 'assessment' },
        { name: 'Weighted Pull-Up', sets: 1, reps: '3RM', intensity: 'Max', details: 'Add load as able. Bodyweight is fine if you can\'t add yet.', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Couch Stretch', duration: '60s/side', format: 'recovery', details: 'Quads + hip flexors after squatting' },
        { name: 'Dead Hang', duration: '30s', format: 'recovery', details: 'Decompress the spine after the pulls' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate' },
      ],
    },
  ],
};

const testingBodyweightDay: WorkoutTemplateDay = {
  name: 'Bodyweight + Work Capacity Test',
  sessionType: 'assessment',
  estimatedDuration: 50,
  blocks: [
    {
      name: 'Movement Prep',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '4 min', format: 'prep', details: 'Build a light sweat before testing' },
        { name: 'Band Pull-Apart', sets: 2, reps: '15', format: 'prep', details: 'Scap + rear delt for the pull-ups' },
        { name: 'Scapular Push-Ups', sets: 2, reps: 10, format: 'prep', details: 'Prime the shoulders + serratus for the push-up test' },
        { name: 'World\'s Greatest Stretch', sets: 1, reps: '5/side', format: 'prep', details: 'Open hips + t-spine before the carry' },
      ],
    },
    {
      name: 'Bodyweight Maxes',
      type: 'testing',
      exercises: [
        { name: 'Push-Ups', sets: 1, reps: 'AMRAP', format: 'bodyweight', details: 'Max strict reps in 2 minutes', sessionType: 'assessment' },
        { name: 'Pull-Ups', sets: 1, reps: 'AMRAP', format: 'bodyweight', details: 'Strict, no kipping. Full extension at the bottom.', sessionType: 'assessment' },
        { name: 'Plank Hold', sets: 1, reps: 'Max Time', format: 'bodyweight', details: 'Stop when form breaks.', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Work Capacity',
      type: 'testing',
      exercises: [
        { name: 'Farmer Carry', sets: 1, reps: '200m', format: 'carry', details: '1.25× bodyweight total load. No setdowns.', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Wall Pec Stretch', duration: '45s/side', format: 'recovery', details: 'Open the chest after the push-ups' },
        { name: 'Dead Hang', duration: '30s', format: 'recovery', details: 'Decompress + flush the grip after the pull-ups + carry' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate' },
      ],
    },
  ],
};

const testingRuckDay: WorkoutTemplateDay = {
  name: 'Ruck / Run Test',
  sessionType: 'assessment',
  estimatedDuration: 60,
  blocks: [
    {
      name: 'Pre-Ruck Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Brisk unloaded walk to raise heart rate before the test' },
        { name: 'Ankle Rocks', sets: 1, reps: '10/side', format: 'prep', details: 'Dorsiflexion for a loaded gait' },
        { name: 'Hip Flexor Opener', duration: '30s/side', format: 'prep', details: 'Open the hips before the loaded carry' },
        { name: 'Glute Bridge', sets: 2, reps: '10', format: 'prep', details: 'Carry the load with the posterior chain' },
      ],
    },
    {
      name: 'Time Trial',
      type: 'testing',
      exercises: [
        { name: '3-Mile Run or 45lb Ruck', duration: 'For Time', format: 'ruck', details: 'Record total time. Choose ONE — run unloaded for speed, OR ruck with 45 lbs (plate carrier or rucksack) for tactical capacity.', sessionType: 'assessment' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Easy Run', duration: '5 min', format: 'cardio', intensity: 'Easy', details: 'Walk it out — bring heart rate down gradually' },
        { name: 'Calf Stretch', duration: '45s/side', format: 'recovery', details: 'Straight + bent knee after the run/ruck' },
        { name: 'Couch Stretch', duration: '60s/side', format: 'recovery', details: 'Hip flexors + quads' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate' },
      ],
    },
  ],
};

const lowerPowerDay: WorkoutTemplateDay = {
  name: 'Lower Body Strength + Power',
  sessionType: 'pt',
  estimatedDuration: 70,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Bike or row — raise core temp' },
        { name: 'Ankle Rocks', sets: 1, reps: '10/side', format: 'prep', details: 'Dorsiflexion for front-squat depth' },
        { name: 'Hip Flexor Opener', duration: '30s/side', format: 'prep', details: 'Open the hips before squatting + hinging' },
        { name: 'Glute Bridge', sets: 2, reps: '10', format: 'prep', details: 'Posterior-chain activation for the deadlift' },
        { name: 'Spanish Squat Iso', sets: 2, reps: '30s', format: 'prep', details: 'Primes quads + patellar tendon — potentiates the jumps, knee-friendly' },
        { name: 'Hip Hinge Drill', sets: 1, reps: 10, format: 'prep', details: 'Groove the hinge before loading the trap bar' },
      ],
    },
    {
      name: 'Lower Body Power',
      type: 'strength',
      exercises: [
        { name: 'Front Squat', sets: 4, reps: 5, intensity: 'progressive', details: 'Controlled descent and drive' },
        { name: 'Trap Bar Deadlift', sets: 4, reps: 3, intensity: 'progressive', details: 'Explosive drive from floor' },
      ],
    },
    {
      name: 'Explosive Movement',
      type: 'power',
      exercises: [
        { name: 'Front Box Jump', sets: 4, reps: 5, intensity: 'explosive', format: 'plyometric', details: 'Single max-height effort, stick the landing, step down, full recovery' },
      ],
    },
    {
      name: 'Core',
      type: 'accessory',
      exercises: [
        { name: 'Dead Bug', sets: 3, reps: '10/side', intensity: 'progressive', rest: '45s', details: 'Anti-extension — brace the trunk, ribs down, low back flat' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Couch Stretch', duration: '60s/side', format: 'recovery', details: 'Quads + hip flexors after squatting + jumping' },
        { name: 'Dead Hang', duration: '30s', format: 'recovery', details: 'Decompress the spine after the trap-bar pulls' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate after the heavy + explosive work' },
      ],
    },
  ],
};

const upperPowerDay: WorkoutTemplateDay = {
  name: 'Upper Body Strength + Power',
  sessionType: 'pt',
  estimatedDuration: 70,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '2 min', format: 'prep', details: 'Rower — warm the upper back' },
        { name: 'Band Pull-Apart', sets: 2, reps: '15', format: 'prep', details: 'Scap retraction + rear delt for the row + pull-up' },
        { name: 'Band External Rotation', sets: 2, reps: '12/side', format: 'prep', details: 'Rotator-cuff prime before incline pressing' },
        { name: 'Scapular Push-Ups', sets: 2, reps: 10, format: 'prep', details: 'Serratus + scap control for pressing' },
        { name: 'T-Spine Extension', sets: 1, reps: '8', format: 'prep', details: 'Thoracic mobility for press + pull' },
      ],
    },
    {
      name: 'Upper Body Power',
      type: 'strength',
      exercises: [
        { name: 'Incline Bench Press', sets: 4, reps: 6, intensity: 'progressive', details: 'Control tempo, full range' },
        { name: 'Weighted Pull-Up', sets: 4, reps: 5, intensity: 'progressive', details: 'Add load, maintain form' },
      ],
    },
    {
      name: 'Explosive Movement',
      type: 'power',
      exercises: [
        { name: 'Med Ball Slam', sets: 4, reps: 10, intensity: 'explosive', format: 'power', details: 'Explosive and fast' },
      ],
    },
    {
      name: 'Horizontal Pull',
      type: 'accessory',
      exercises: [
        { name: 'Chest Supported Row', sets: 3, reps: 10, intensity: 'progressive', rest: '90s', details: 'Balances the incline pressing — pause at the chest, no torso english' },
      ],
    },
    {
      name: 'Core',
      type: 'accessory',
      exercises: [
        { name: 'Pallof Press', sets: 3, reps: '12/side', intensity: 'progressive', rest: '60s', details: 'Anti-rotation — slow, strict, resist the twist' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Wall Pec Stretch', duration: '45s/side', format: 'recovery', details: 'Open the chest after pressing' },
        { name: 'Dead Hang', duration: '30s', format: 'recovery', details: 'Decompress + flush the lats after the pull-ups' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate' },
      ],
    },
  ],
};

const conditioningDay: WorkoutTemplateDay = {
  name: 'Conditioning',
  sessionType: 'pt',
  estimatedDuration: 50,
  blocks: [
    {
      // Was missing entirely — a cold sprint/ruck circuit is a torn-hamstring
      // setup. Strides ramp the tissue before any all-out running.
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Jog or bike — build a light sweat' },
        { name: 'Leg Swings', sets: 2, reps: '10/side', format: 'prep', details: 'Front-back + lateral — dynamic hips' },
        { name: 'Glute Bridge', sets: 1, reps: '15', format: 'prep', details: 'Glute activation before sprinting + swinging' },
        { name: 'World\'s Greatest Stretch', sets: 1, reps: '5/side', format: 'prep', details: 'Open hips + t-spine' },
        { name: 'Strides', sets: 4, reps: '40m', format: 'prep', details: 'Build 60% → 90% before the sprint work — protects the hamstrings' },
      ],
    },
    {
      name: 'Circuit',
      type: 'circuit',
      format: 'circuit',
      exercises: [
        { name: 'Ruck Intervals', sets: 5, reps: '400m', format: 'intervals', details: 'Alternate hard ruck pace and jog recovery, 25-35 lb ruck' },
        { name: 'Farmer Carry', sets: 4, reps: '100m', format: 'circuit', details: 'Sprint 100m out, then heavy farmer carry 100m back — moderate-heavy load' },
        { name: 'Kettlebell Swing', sets: 4, reps: 15, format: 'circuit', details: 'Explosive hip drive' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Hamstring Stretch', duration: '45s/side', format: 'recovery', details: 'Hamstrings take the brunt of the sprints + rucking' },
        { name: 'Calf Stretch', duration: '45s/side', format: 'recovery', details: 'Straight + bent knee' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate the effort' },
      ],
    },
  ],
};

// Work Capacity Day — trimmed to 17 working sets (was 20). Old version
// stacked 4×6 Push-Press + 3×12 Step-Ups + 5×250m Row/Bike inside 45 min,
// which is a 20-set hybrid that compromised lower-body recovery for the
// next Lower Power day. Step-Ups dropped to 2×12 and rest period spelled
// out so users actually recover between high-intensity efforts.
const workCapacityDay: WorkoutTemplateDay = {
  name: 'Work Capacity',
  sessionType: 'pt',
  estimatedDuration: 55,
  blocks: [
    {
      name: 'Warm-Up',
      type: 'prep',
      exercises: [
        { name: 'Easy Cardio', duration: '3 min', format: 'prep', details: 'Bike or row — raise core temp' },
        { name: 'Leg Swings', sets: 2, reps: '10/side', format: 'prep', details: 'Dynamic hips before the step-ups' },
        { name: 'Glute Bridge', sets: 2, reps: '10', format: 'prep', details: 'Posterior-chain activation' },
        { name: 'Band External Rotation', sets: 2, reps: '12/side', format: 'prep', details: 'Cuff prime before the overhead push-press' },
        { name: 'Band Pull-Apart', sets: 2, reps: '15', format: 'prep', details: 'Scap + rear delt before pressing overhead' },
      ],
    },
    {
      name: 'Hybrid Circuit',
      type: 'circuit',
      format: 'circuit',
      exercises: [
        { name: 'Push-Press', sets: 4, reps: 6, intensity: 'progressive', details: 'Explosive overhead drive, 90s rest between sets' },
        { name: 'Step-Ups (Weighted)', sets: 2, reps: 12, format: 'circuit', details: 'Alternate legs, controlled pace, moderate load' },
        { name: 'Row/Bike Sprint', sets: 5, reps: '250m', format: 'intervals', details: 'Full effort, 1:1 work:rest' },
      ],
    },
    {
      name: 'Cooldown',
      type: 'recovery',
      exercises: [
        { name: 'Couch Stretch', duration: '60s/side', format: 'recovery', details: 'Hip flexors + quads after the step-ups' },
        { name: 'Wall Pec Stretch', duration: '45s/side', format: 'recovery', details: 'Open the chest after the overhead work' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate after the high-intensity work' },
      ],
    },
  ],
};

const enduranceDay: WorkoutTemplateDay = {
  name: 'Endurance + Mobility',
  sessionType: 'pt',
  estimatedDuration: 50,
  blocks: [
    {
      name: 'Aerobic Base',
      type: 'conditioning',
      exercises: [
        { name: 'Run or Heavy Ruck', duration: '3-4 miles', format: 'ruck', details: 'Sustainable Zone 2 pace — conversational' },
      ],
    },
    {
      name: 'Core',
      type: 'accessory',
      exercises: [
        { name: 'Plank', sets: 3, reps: '45s', format: 'bodyweight', rest: '45s', details: 'Anti-extension — braced, ribs down, no sag' },
        { name: 'Side Plank', sets: 3, reps: '30s each', format: 'bodyweight', rest: '30s', details: 'Anti-lateral-flexion — stack the hips, hold tall' },
      ],
    },
    {
      name: 'Recovery',
      type: 'recovery',
      exercises: [
        { name: 'Calf Stretch', duration: '45s/side', format: 'recovery', details: 'Straight + bent knee after the miles' },
        { name: 'Couch Stretch', duration: '60s/side', format: 'recovery', details: 'Hip flexors + quads' },
        { name: 'Hamstring Stretch', duration: '45s/side', format: 'recovery', details: 'Decompress after the run/ruck' },
        { name: '90/90 Breathing', duration: '2 min', format: 'recovery', details: 'Down-regulate' },
      ],
    },
  ],
};

const restDay: WorkoutTemplateDay = {
  name: 'Rest Day',
  sessionType: 'pt',
  estimatedDuration: 0,
  blocks: [],
};

export const specialistProgram12Week: WorkoutProgram = {
  metadata,
  periodization,
  templates: {
    // Testing weeks (W1, W12) — split across Mon/Wed/Fri so each test is
    // a fresh max-effort. Cramming everything into Day 1 was producing
    // noisy benchmarks and burning users out before the program even
    // started. Tue/Thu/Sat/Sun rest, same as a normal Specialist week.
    'testing-week-day1': testingStrengthDay,
    'testing-week-day2': restDay,
    'testing-week-day3': testingBodyweightDay,
    'testing-week-day4': restDay,
    'testing-week-day5': testingRuckDay,
    'testing-week-day6': restDay,
    'testing-week-day7': restDay,

    // Training cycle (W2-11)
    'training-day1': lowerPowerDay,
    'training-day2': upperPowerDay,
    'training-day3': conditioningDay,
    'training-day4': workCapacityDay,
    'training-day5': enduranceDay,
    'training-day6': restDay,
    'training-day7': restDay,
  },
  workouts: [],
};

